import type { BotSettings } from "@ai-admin/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { queryClient, trpc } from "../../../app/trpc.js";
import { Card, Input, Label, Notice, Spinner } from "../../../ui/index.js";
import { StepNav } from "../StepNav.js";
import type { StepProps } from "../types.js";
import { useUnsavedChanges } from "../unsaved.js";

const MODES = ["auto", "confirm", "answer_only"] as const satisfies readonly BotSettings["autonomy"][];

/** Шаг после правил: как ассистент записывает и сколько минут ждёт мастера. Выключатель и остальное в «Ещё». */
export function AssistantStep({ salonId, next, back }: StepProps) {
  const { t } = useTranslation();
  const bot = useQuery(trpc.bot.settings.get.queryOptions({ salonId }));
  const [autonomy, setAutonomy] = useState<BotSettings["autonomy"] | null>(null);
  const [silence, setSilence] = useState("");
  useEffect(() => {
    if (!bot.data || autonomy !== null) return;
    setAutonomy(bot.data.autonomy);
    setSilence(String(bot.data.masterSilenceMin));
  }, [bot.data, autonomy]);
  useUnsavedChanges(bot.data !== undefined && autonomy !== null && (autonomy !== bot.data.autonomy || silence !== String(bot.data.masterSilenceMin)));
  const save = useMutation(
    trpc.bot.settings.update.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.bot.settings.pathFilter());
        next({ saved: true });
      },
    }),
  );

  if (bot.isError) return <Notice kind="error">{t("common.error")}</Notice>;
  if (bot.isPending || autonomy === null) return <Spinner />;
  const minutes = Number(silence);
  const valid = silence.trim() !== "" && Number.isInteger(minutes) && minutes >= 0 && minutes <= 240;
  const onNext = () => {
    if (!valid) return;
    save.mutate({ salonId, patch: { autonomy, masterSilenceMin: minutes } });
  };

  return (
    <div>
      <p className="mb-4 text-slate-600">{t("onb.assistant.intro")}</p>
      <div className="space-y-2">
        {MODES.map((mode) => (
          <button key={mode} type="button" aria-pressed={autonomy === mode} onClick={() => setAutonomy(mode)} className="block w-full text-left">
            <Card className={autonomy === mode ? "border-slate-900 ring-2 ring-slate-200" : ""}>
              <div className="font-medium">{t(`onb.opt.autonomy.${mode}`)}</div>
            </Card>
          </button>
        ))}
      </div>
      <div className="mt-3">
        <Label htmlFor="assistant-silence">{t("bot.masterSilence")}</Label>
        <Input id="assistant-silence" inputMode="numeric" value={silence} onChange={(e) => setSilence(e.target.value.replace(/\D/g, ""))} />
        <p className="mt-1 text-sm text-slate-500">{t("bot.masterSilenceHint")}</p>
        {!valid && <p className="mt-1 text-sm text-amber-700">{t("onb.assistant.badMinutes")}</p>}
      </div>
      {save.isError && <Notice kind="error">{t("common.error")}</Notice>}
      <StepNav onBack={back} onNext={onNext} disabled={!valid} busy={save.isPending} />
    </div>
  );
}
